/**
 * Derivation for the header sync-state note (DV-2221 / channel-aware follow-up).
 *
 * CHANNEL-AWARE RULE (docs/superpowers/specs/
 * 2026-07-20-channel-aware-sync-badge-design.md, spec §4): the direct PMS
 * sync can only deliver SOME edits (new-photo upload, hide/unhide via
 * Buildium/AppFolio delete-hidden-synced-photos — not Rentvine — and, since
 * DV-2799, the photo order); caption edits of already-synced photos never
 * reach any PMS (the caption is frozen into the filename at first upload). The legacy
 * webhook snapshot, by contrast, carries every badge-raising edit. So the
 * badge lights per-channel: the webhook side on any pending edit, the PMS
 * side only on a PMS-deliverable pending edit — each gated on the entity
 * actually having that channel. A missing backend flag (pre-Phase-1 QA data)
 * reads as false, so the PMS side simply stays dark rather than false-lighting.
 *
 * DV-2799: a reorder now counts as PMS-deliverable — the backend raises
 * hasUnsyncedPmsChanges for it, and the slice marks dirtyPms to match, so a
 * PMS-only entity sees the badge right after a re-sequence. Caption edits
 * remain webhook-only.
 */
export interface BadgeInput {
  hasWebhooks: boolean;
  isPmsSynced: boolean;
  /** photoset.hasUnsyncedChanges — webhook-channel flag (any content edit). */
  hasBackendUnsynced: boolean;
  /** photoset.hasUnsyncedPmsChanges — PMS-channel flag (upload/hide/staging/reorder). */
  hasBackendPmsUnsynced: boolean;
  /** Session-local dirty edits of any kind (captions, order, hides). */
  dirtyCount: number;
  /** Session-local dirty edits the PMS sync can deliver (uploads, staging commits, hides, reorders). */
  pmsDirtyCount: number;
}

export function shouldShowUnsyncedBadge(input: BadgeInput): boolean {
  const webhookSide =
    input.hasWebhooks && (input.hasBackendUnsynced || input.dirtyCount > 0);
  const pmsSide =
    input.isPmsSynced && (input.hasBackendPmsUnsynced || input.pmsDirtyCount > 0);
  return webhookSide || pmsSide;
}

/**
 * The note component itself (badge OR last-synced line) renders for any entity
 * with a delivery channel — channel-aware: PMS-only entities keep the
 * "Photos last synced…" line, and the badge itself lights only when that
 * entity has a deliverable pending edit on a channel it actually has (see
 * shouldShowUnsyncedBadge).
 */
export function shouldShowSyncStateNote(input: BadgeInput): boolean {
  return input.hasWebhooks || input.isPmsSynced;
}

/**
 * Copy contract for the counted line ("N unsynced changes"): the count must
 * only cover edits the entity's lit channel(s) can actually deliver — "if
 * it's lit, syncing will fix it." A webhook-capable entity's webhook push
 * delivers every session-dirty edit, so the full dirtyCount applies. A
 * PMS-only entity's direct sync only delivers PMS-deliverable edits, so it
 * must count pmsDirtyCount, not dirtyCount — otherwise a PMS-only entity with
 * caption edits (never PMS-deliverable) would promise a fix that
 * syncing can't provide. An entity with neither channel shows no count.
 */
export function visibleDirtyCount(input: BadgeInput): number {
  if (input.hasWebhooks) {
    return input.dirtyCount;
  }
  if (input.isPmsSynced) {
    return input.pmsDirtyCount;
  }
  return 0;
}
